import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ElementRef,
    OnInit,
    ViewChild,
    inject
} from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ToolbarComponent } from '../../../shared/components/toolbar/toolbar.component';
import { ToastService } from '../../../shared/services/toast.service';
import { IPost, IPostUpsert, PostVideoType } from '../../model/post.model';
import { PostService } from '../../services/post.service';
import { environment } from '../../../environments/environment';
import { ICommunity } from '../../../community/model/community.model';
import { CommunityService } from '../../../community/services/community.service';

@Component({
    selector: 'clp-community-post-details',
    styleUrls: ['./communityPostDetails.component.scss'],
    templateUrl: './communityPostDetails.component.html',
    imports: [
        RouterModule,
        TranslateModule,
        CommonModule,
        ReactiveFormsModule,
        ToolbarComponent
    ],
    providers: [
        CommunityService,
        PostService,
        ToastrService
    ]
})
export class CommunityPostDetailsComponent implements OnInit, AfterViewInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private postService = inject(PostService);
    private communityService = inject(CommunityService);
    private toastService = inject(ToastService);
    private translate = inject(TranslateService);

    @ViewChild('editor') editor?: ElementRef<HTMLDivElement>;

    postForm = new FormGroup({
        content: new FormControl('', [Validators.required, Validators.minLength(3)]),
        communityIds: new FormControl<string[]>([], [Validators.required]),
        videoType: new FormControl(PostVideoType.None, { nonNullable: true }),
        videoLink: new FormControl('')
    });

    id: string | null = null;
    communityId: string | null = null;
    communities: ICommunity[] = [];
    saving = false;
    selectedImageFile: File | null = null;
    imagePreview: string | null = null;
    currentImageFile: string | null = null;
    readonly postVideoType = PostVideoType;
    isCodeView = false;
    codeViewContent = '';
    selectedFontFamily = 'inherit';
    selectedTextColor = '#e2e8f0';
    selectedBackgroundColor = '#1e293b';
    readonly fontFamilies = [
        { label: 'Default', value: 'inherit' },
        { label: 'Serif', value: 'Georgia, serif' },
        { label: 'Sans', value: 'Arial, Helvetica, sans-serif' },
        { label: 'Monospace', value: 'Courier New, monospace' },
        { label: 'Merriweather', value: 'Merriweather, Georgia, serif' },
        { label: 'Lora', value: 'Lora, Georgia, serif' }
    ];
    readonly blockFormats = [
        { label: 'P', value: 'P' },
        { label: 'H1', value: 'H1' },
        { label: 'H2', value: 'H2' },
        { label: 'H3', value: 'H3' },
        { label: 'H4', value: 'H4' },
        { label: 'H5', value: 'H5' },
        { label: 'H6', value: 'H6' },
        { label: 'Quote', value: 'BLOCKQUOTE' },
        { label: 'Code', value: 'PRE' }
    ];
    selectedBlockFormat = 'P';
    private savedRange: Range | null = null;

    ngOnInit(): void {
        this.id = this.route.snapshot.paramMap.get('id');
        this.communityId = this.route.snapshot.paramMap.get('communityId');
        const queryCommunityId = this.route.snapshot.queryParamMap.get('communityId');
        const initialCommunityId = queryCommunityId ?? this.communityId;
        if (initialCommunityId) {
            this.postForm.controls.communityIds.setValue([initialCommunityId]);
        }

        this.communityService.getCommunities().subscribe((communities) => {
            this.communities = communities ?? [];
        });

        if (this.id) {
            this.postService.getPost(this.id).subscribe((post) => {
                if (!post) {
                    return;
                }

                this.patchForm(post);
            });
        }
    }

    ngAfterViewInit(): void {
        this.syncEditorWithForm();
    }

    get contentControl(): AbstractControl<string | null, string | null> | null {
        return this.postForm.get('content');
    }

    get communityIdsControl(): AbstractControl<string[] | null, string[] | null> | null {
        return this.postForm.get('communityIds');
    }

    isCommunitySelected(communityId: string | undefined): boolean {
        if (!communityId) {
            return false;
        }

        return (this.postForm.controls.communityIds.value ?? []).includes(communityId);
    }

    onCommunitySelectionChange(communityId: string | undefined, selected: boolean): void {
        if (!communityId) {
            return;
        }

        const current = new Set(this.postForm.controls.communityIds.value ?? []);
        if (selected) {
            current.add(communityId);
        } else {
            current.delete(communityId);
        }

        this.postForm.controls.communityIds.setValue(Array.from(current));
        this.postForm.controls.communityIds.markAsTouched();
    }

    onEditorInput(event: Event): void {
        const value = (event.target as HTMLDivElement).innerHTML;
        this.updateEditorValue(value);
    }

    onEditorSelectionChange(): void {
        this.saveSelection();
    }

    onToolbarMouseDown(event: MouseEvent): void {
        event.preventDefault();
        this.restoreSelection();
    }

    onToolbarControlMouseDown(): void {
        this.saveSelection();
    }

    applyFormat(command: string, value?: string): void {
        if (this.isCodeView) {
            return;
        }

        this.ensureEditorSelection();
        document.execCommand(command, false, value);
        this.editor?.nativeElement.focus({ preventScroll: true });
        this.updateEditorValue(this.editor?.nativeElement.innerHTML ?? '');
        this.saveSelection();
    }

    applyBlockFormat(value: string): void {
        if (this.isCodeView) {
            return;
        }

        this.ensureEditorSelection();
        const wrapped = `<${value}>`;
        document.execCommand('formatBlock', false, wrapped);

        this.selectedBlockFormat = value;
        this.editor?.nativeElement.focus({ preventScroll: true });
        this.updateEditorValue(this.editor?.nativeElement.innerHTML ?? '');
        this.saveSelection();
    }

    setFontFamily(value: string): void {
        this.selectedFontFamily = value;
        if (value === 'inherit') {
            this.applyFormat('removeFormat');
            return;
        }

        this.applyFormat('fontName', value);
    }

    setTextColor(value: string): void {
        this.selectedTextColor = value;
        this.applyFormat('foreColor', value);
    }

    setBackgroundColor(value: string): void {
        this.selectedBackgroundColor = value;
        this.applyFormat('hiliteColor', value);
    }

    onBlockFormatChange(event: Event): void {
        const value = (event.target as HTMLSelectElement).value;
        this.applyBlockFormat(value);
    }

    onFontFamilyChange(event: Event): void {
        const value = (event.target as HTMLSelectElement).value;
        this.setFontFamily(value);
    }

    onTextColorChange(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.setTextColor(value);
    }

    onBackgroundColorChange(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.setBackgroundColor(value);
    }

    toggleCodeView(): void {
        if (!this.isCodeView) {
            this.codeViewContent = this.editor?.nativeElement.innerHTML ?? this.postForm.controls.content.value ?? '';
            this.isCodeView = true;
            return;
        }

        this.isCodeView = false;
        this.syncEditorWithForm(this.codeViewContent);
        this.editor?.nativeElement.focus({ preventScroll: true });
        this.saveSelection();
    }

    onCodeInput(event: Event): void {
        const value = (event.target as HTMLTextAreaElement).value;
        this.codeViewContent = value;
        this.updateEditorValue(value);
    }

    insertLink(): void {
        if (this.isCodeView) {
            return;
        }

        this.ensureEditorSelection();
        const url = window.prompt(this.translate.instant('post.editor.linkPrompt'));
        if (!url) {
            return;
        }

        this.applyFormat('createLink', url);
    }

    insertHorizontalRule(): void {
        this.applyFormat('insertHorizontalRule');
    }

    insertTable(): void {
        if (this.isCodeView) {
            return;
        }

        this.ensureEditorSelection();

        const rowsInput = window.prompt('Rows', '3');
        const colsInput = window.prompt('Columns', '3');
        const rows = Math.min(10, Math.max(1, Number(rowsInput ?? 0)));
        const cols = Math.min(10, Math.max(1, Number(colsInput ?? 0)));
        if (!rows || !cols || Number.isNaN(rows) || Number.isNaN(cols)) {
            return;
        }

        const headerCells = Array.from({ length: cols })
            .map((_, idx) => `<th>Header ${idx + 1}</th>`)
            .join('');
        const bodyRows = Array.from({ length: rows - 1 })
            .map(() => `<tr>${Array.from({ length: cols }).map(() => '<td>Cell</td>').join('')}</tr>`)
            .join('');
        const tableHtml = `<table class="clp-table clp-table-striped clp-table-hover clp-table-sm"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table><p><br></p>`;

        document.execCommand('insertHTML', false, tableHtml);
        this.updateEditorValue(this.editor?.nativeElement.innerHTML ?? '');
        this.saveSelection();
    }

    clearFormatting(): void {
        if (this.isCodeView) {
            return;
        }

        this.ensureEditorSelection();
        document.execCommand('unlink');
        document.execCommand('removeFormat');
        this.updateEditorValue(this.editor?.nativeElement.innerHTML ?? '');
        this.saveSelection();
    }

    removeLink(): void {
        if (this.isCodeView || !this.editor) {
            return;
        }

        this.ensureEditorSelection();
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            return;
        }

        const node = selection.anchorNode instanceof Element
            ? selection.anchorNode
            : selection.anchorNode?.parentElement;
        const anchor = node?.closest('a');
        if (anchor) {
            const text = anchor.textContent ?? '';
            anchor.replaceWith(document.createTextNode(text));
            this.updateEditorValue(this.editor.nativeElement.innerHTML);
            this.saveSelection();
            return;
        }

        document.execCommand('unlink');
        this.updateEditorValue(this.editor.nativeElement.innerHTML);
        this.saveSelection();
    }

    addTableRow(position: 'above' | 'below'): void {
        if (this.isCodeView) {
            return;
        }

        const cell = this.getCurrentCell();
        if (!cell) {
            return;
        }

        const row = cell.parentElement as HTMLTableRowElement | null;
        if (!row) {
            return;
        }

        const table = row.closest('table');
        if (!table) {
            return;
        }

        const newRow = document.createElement('tr');
        const colCount = row.cells.length || 1;
        for (let i = 0; i < colCount; i++) {
            const newCell = document.createElement('td');
            newCell.textContent = 'Cell';
            newRow.appendChild(newCell);
        }

        if (position === 'above') {
            row.parentElement?.insertBefore(newRow, row);
        } else {
            row.parentElement?.insertBefore(newRow, row.nextSibling);
        }

        this.focusElement(newRow.cells[0]);
        this.updateEditorValue(this.editor?.nativeElement.innerHTML ?? '');
        this.saveSelection();
    }

    removeTableRow(): void {
        if (this.isCodeView) {
            return;
        }

        const cell = this.getCurrentCell();
        if (!cell) {
            return;
        }

        const row = cell.parentElement as HTMLTableRowElement | null;
        const section = row?.parentElement;
        if (!row || !section) {
            return;
        }

        if (section.children.length <= 1) {
            const table = row.closest('table');
            table?.remove();
        } else {
            row.remove();
        }

        this.editor?.nativeElement.focus({ preventScroll: true });
        this.updateEditorValue(this.editor?.nativeElement.innerHTML ?? '');
        this.saveSelection();
    }

    addTableColumn(position: 'left' | 'right'): void {
        if (this.isCodeView) {
            return;
        }

        const cell = this.getCurrentCell();
        if (!cell) {
            return;
        }

        const row = cell.parentElement as HTMLTableRowElement | null;
        const table = row?.closest('table');
        if (!row || !table) {
            return;
        }

        const columnIndex = cell.cellIndex;
        const insertAt = position === 'left' ? columnIndex : columnIndex + 1;
        const rows = Array.from(table.rows);
        rows.forEach((currentRow) => {
            const newCellTag = currentRow.parentElement?.tagName === 'THEAD' ? 'th' : 'td';
            const newCell = document.createElement(newCellTag);
            newCell.textContent = newCellTag === 'th' ? 'Header' : 'Cell';
            const refCell = currentRow.cells[insertAt] ?? null;
            currentRow.insertBefore(newCell, refCell);
        });

        const targetRow = row;
        const targetCell = targetRow.cells[insertAt] ?? targetRow.cells[Math.max(0, insertAt - 1)];
        this.focusElement(targetCell);
        this.updateEditorValue(this.editor?.nativeElement.innerHTML ?? '');
        this.saveSelection();
    }

    removeTableColumn(): void {
        if (this.isCodeView) {
            return;
        }

        const cell = this.getCurrentCell();
        if (!cell) {
            return;
        }

        const row = cell.parentElement as HTMLTableRowElement | null;
        const table = row?.closest('table');
        if (!row || !table) {
            return;
        }

        const columnIndex = cell.cellIndex;
        const rows = Array.from(table.rows);
        rows.forEach((currentRow) => {
            if (currentRow.cells.length > columnIndex) {
                currentRow.deleteCell(columnIndex);
            }
        });

        const hasAnyCells = Array.from(table.rows).some((currentRow) => currentRow.cells.length > 0);
        if (!hasAnyCells) {
            table.remove();
            this.editor?.nativeElement.focus({ preventScroll: true });
        } else {
            const fallbackRow = table.rows[0];
            const fallbackCell = fallbackRow?.cells[Math.max(0, Math.min(columnIndex, fallbackRow.cells.length - 1))];
            this.focusElement(fallbackCell);
        }

        this.updateEditorValue(this.editor?.nativeElement.innerHTML ?? '');
        this.saveSelection();
    }

    onImageChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] ?? null;
        this.selectedImageFile = file;

        if (!file) {
            this.imagePreview = this.getCurrentImageUrl();
            return;
        }

        this.imagePreview = URL.createObjectURL(file);
    }

    removeImage(): void {
        this.selectedImageFile = null;
        this.currentImageFile = null;
        this.imagePreview = null;
    }

    savePost(): void {
        const selectedCommunityIds = this.postForm.controls.communityIds.value ?? [];
        if (this.postForm.invalid || selectedCommunityIds.length === 0) {
            this.postForm.markAllAsTouched();
            return;
        }

        const payload: IPostUpsert = {
            id: this.id ?? undefined,
            groupId: selectedCommunityIds[0] ?? null,
            groupIds: selectedCommunityIds,
            communityIds: selectedCommunityIds,
            content: this.postForm.controls.content.value ?? '',
            publicPost: false,
            publishOnNostr: false,
            videoType: this.postForm.controls.videoType.value,
            videoLink: this.postForm.controls.videoLink.value,
            imageFile: this.selectedImageFile,
            currentImageFile: this.currentImageFile
        };

        const request$ = this.id
            ? this.postService.updatePost(payload)
            : this.postService.createPost(payload);

        this.saving = true;
        request$.subscribe({
            next: () => {
                this.saving = false;
                const message = this.id
                    ? 'post.update.successMessage'
                    : 'post.create.successMessage';
                this.toastService.show(this.translate.instant(message), '');
                const returnCommunityId = this.communityId ?? selectedCommunityIds[0] ?? null;
                this.router.navigate(['/communityPosts'], { queryParams: returnCommunityId ? { communityId: returnCommunityId } : null });
            },
            error: () => {
                this.saving = false;
            }
        });
    }

    private patchForm(post: IPost): void {
        this.postForm.patchValue({
            content: post.content ?? '',
            communityIds: this.resolvePostCommunityIds(post),
            videoType: this.normalizeVideoType(post.videoType),
            videoLink: post.videoLink ?? ''
        });

        this.currentImageFile = post.imageFile ?? null;
        this.imagePreview = this.getCurrentImageUrl();
        this.syncEditorWithForm(post.content ?? '');
    }

    private syncEditorWithForm(content?: string): void {
        if (!this.editor) {
            return;
        }

        const html = content ?? this.postForm.controls.content.value ?? '';
        this.editor.nativeElement.innerHTML = html;
        this.updateEditorValue(html);
    }

    private updateEditorValue(content: string): void {
        this.postForm.controls.content.setValue(content);
        this.postForm.controls.content.markAsTouched();
    }

    private saveSelection(): void {
        if (!this.editor || this.isCodeView) {
            return;
        }

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            return;
        }

        const range = selection.getRangeAt(0);
        if (this.editor.nativeElement.contains(range.commonAncestorContainer)) {
            this.savedRange = range;
        }
    }

    private restoreSelection(): boolean {
        if (!this.savedRange || !this.editor || this.isCodeView) {
            return false;
        }

        const selection = window.getSelection();
        if (!selection) {
            return false;
        }

        selection.removeAllRanges();
        selection.addRange(this.savedRange);
        return true;
    }

    private ensureEditorSelection(): void {
        if (!this.editor || this.isCodeView) {
            return;
        }

        this.editor.nativeElement.focus({ preventScroll: true });
        if (!this.restoreSelection()) {
            this.placeCaretAtEnd(this.editor.nativeElement);
        }
    }

    private placeCaretAtEnd(element: HTMLElement): void {
        const selection = window.getSelection();
        if (!selection) {
            return;
        }

        const range = document.createRange();
        range.selectNodeContents(element);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
        this.savedRange = range;
    }

    private getCurrentCell(): HTMLTableCellElement | null {
        if (!this.editor || this.isCodeView) {
            return null;
        }

        this.ensureEditorSelection();
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            return null;
        }

        const node = selection.anchorNode instanceof Element
            ? selection.anchorNode
            : selection.anchorNode?.parentElement;

        return (node?.closest('td,th') as HTMLTableCellElement | null) ?? null;
    }

    private focusElement(element?: Element | null): void {
        if (!element) {
            this.editor?.nativeElement.focus({ preventScroll: true });
            return;
        }

        const selection = window.getSelection();
        if (!selection) {
            return;
        }

        const range = document.createRange();
        range.selectNodeContents(element);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        this.savedRange = range;
        this.editor?.nativeElement.focus({ preventScroll: true });
    }

    private getCurrentImageUrl(): string | null {
        if (!this.currentImageFile) {
            return null;
        }

        return `${environment.API_URL}/posts/images/${this.currentImageFile}`;
    }

    private resolvePostCommunityIds(post: IPost): string[] {
        const ids = post.communityIds ?? post.groupIds ?? (post.groupId ? [post.groupId] : []);
        const normalized = Array.from(new Set((ids ?? []).map((id) => `${id ?? ''}`.trim()).filter((id) => !!id)));
        if (normalized.length > 0) {
            return normalized;
        }

        return this.communityId ? [this.communityId] : [];
    }

    private normalizeVideoType(value: PostVideoType | number | null | undefined): PostVideoType {
        if (typeof value === 'number' && Number.isFinite(value)) {
            if (value === PostVideoType.YouTube || value === PostVideoType.Vimeo) {
                return value;
            }
            return PostVideoType.None;
        }

        const raw = `${value ?? ''}`.trim().toLowerCase();
        if (raw === 'youtube' || raw === String(PostVideoType.YouTube)) {
            return PostVideoType.YouTube;
        }
        if (raw === 'vimeo' || raw === String(PostVideoType.Vimeo)) {
            return PostVideoType.Vimeo;
        }

        return PostVideoType.None;
    }
}
